import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import AdminLayout from './AdminLayout';
import { STUDENT_ELEARNING_MENU } from 'constants/menu';
import { ELEARNING_POLL_INTERVAL } from 'constants/driving-elearning.constant';
import elearningApi from 'api/elearningApi';
import { Button, Image } from 'react-bootstrap';
import { PATH } from 'constants/path';
import { useDispatch, useSelector } from 'react-redux';
import { selectElearningData, selectElearningUser, updateElearningData } from 'store/elearning.slice';
import {
  groupCourseContent,
  groupUserGradeByCourseModule,
  getTodayRangeUTC7,
} from 'utils/elearning.utils';
// moodleApi import dropped after Phase 2A — every call site on this layout
// now goes through the BE proxy via elearningApi.
import { toastWrapper } from 'utils';
import { useNavigate } from 'react-router-dom';
import useElearningSession from 'hooks/useElearningSession';
import SessionKickedOverlay from 'features/elearning/components/SessionKickedOverlay';
import InfoConfirmationRedirect from 'features/elearning/components/InfoConfirmationRedirect';
import ChatProvider from 'features/chat/ChatProvider';
import ChatPopup from 'features/chat/components/ChatPopup';
import {
  DAILY_LIMIT_EVENT, shouldBlockFromServer, vnDateKey, msUntilNextVnReset, midnightRetryDelayMs, isSavedBlockStale,
} from 'utils/daily-limit-event.util';
import { centerZaloNumber } from 'utils/center-contact.util';

// Skeleton shimmer cho logo trung tâm trong title — tránh flash-of-
// broken-image hoặc layout shift trong lúc browser fetch logo URL từ
// S3/CDN. Pattern khớp với avatar skeleton ở ElearningStudentMyPage.
const logoShimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const LOGO_SIZE = 64; // px — khớp với AdminLayout LogoArea img max-height (60px)
                      // + chút padding visual để logo không sát cạnh khung tròn.

const LogoWrap = styled.div`
  position: relative;
  display: inline-block;
  width: ${LOGO_SIZE}px;
  height: ${LOGO_SIZE}px;
  border-radius: 50%;
  overflow: hidden;
`;

const LogoSkeleton = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: ${logoShimmer} 1.4s ease-in-out infinite;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  /* object-fit: cover để logo non-square crop vào trong khung tròn —
     đa số logo trung tâm là vuông/tròn nên cover ít distort. Nếu cần
     letterbox (giữ aspect, có khoảng trống) thì đổi sang contain. */
  object-fit: cover;
  border-radius: 50%;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 250ms ease-in-out;
`;

// Floating action icon (Phone / Zalo) — khớp hiệu ứng hover của Fab
// trong ChatPopup: scale 1.08 + lift box-shadow trong 0.15s. Trước đây
// 2 icon này dùng raw <Button> không có animation, cảm giác tĩnh so
// với ChatPopup Fab có hover. Pattern: anchor + img tròn 50×50.
const FloatingActionIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.15s ease, filter 0.15s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.15s ease;
    display: block;
  }

  &:hover {
    transform: scale(1.08);
  }

  &:hover img {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22);
  }

  &:active {
    transform: scale(0.96);
  }
`;

function CenterLogo({ src }) {
  const [loaded, setLoaded] = useState(false);
  // Reset loaded khi src đổi (vd HV chuyển center, hoặc store update).
  useEffect(() => { setLoaded(false); }, [src]);
  if (!src) return null;
  return (
    <LogoWrap>
      {!loaded && <LogoSkeleton />}
      <LogoImage
        src={src}
        alt='Logo'
        $loaded={loaded}
        onLoad={() => setLoaded(true)}
        // onError: ẩn skeleton để khỏi loop shimmer mãi nếu logo URL chết.
        // Logo silently disappear (alt='Logo' không có visual fallback).
        onError={() => setLoaded(true)}
      />
    </LogoWrap>
  );
}

function StudentElearningLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const student = useSelector(selectElearningUser);
  const elearningData = useSelector(selectElearningData);
  const { activityReport, elearningGrades, elearningSettings, quizAttempts, bookMapaData, bookModuleSettings, isLimitExceeded, elearningUser, elearningCoursesContents } = elearningData;

  const POLL_INTERVAL = ELEARNING_POLL_INTERVAL;
  // Đang bị chặn: hỏi giờ mỗi 3 lượt (~30 phút) thay vì mỗi lượt.
  const BLOCKED_POLL_EVERY = 3;

  const fetchTodayTimeFromServer = React.useCallback(async () => {
    try {
      const res = await elearningApi.getMyTodayTime();
      const { totalTodayTime, timeLimitPerDay, timePerCourse } = res.data;

      // Chặn theo cờ của server (dừng ghi giờ đúng ở giới hạn), không đoán
      // trước — trước đây chặn sớm 10 phút.
      const shouldBlock = shouldBlockFromServer(res.data);

      // Lưu trạng thái block vào localStorage để giữ khi reload
      if (shouldBlock) {
        localStorage.setItem('elearning_limit_blocked', JSON.stringify({
          blocked: true,
          date: vnDateKey(),
        }));
      } else {
        localStorage.removeItem('elearning_limit_blocked');
      }

      dispatch(
        updateElearningData({
          totalTodayTime,
          timeLimitPerDay,
          isLimitExceeded: shouldBlock,
          timePerCourse,
          todayTimeUpdatedAt: Date.now(),
        })
      );
      return shouldBlock;
    } catch (error) {
      console.error('Error fetching today time from server:', error);
      return null;
    }
  }, [dispatch]);

  const isLimitExceededRef = React.useRef(isLimitExceeded);
  React.useEffect(() => {
    isLimitExceededRef.current = isLimitExceeded;
  }, [isLimitExceeded]);

  // Khôi phục trạng thái block từ localStorage khi reload (chỉ nếu cùng ngày)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('elearning_limit_blocked'));
      if (saved?.blocked) {
        if (!isSavedBlockStale(saved)) {
          dispatch(updateElearningData({ isLimitExceeded: true }));
        } else {
          localStorage.removeItem('elearning_limit_blocked');
        }
      }
    } catch {}
  }, []);

  // Server báo vừa chạm giới hạn giờ/ngày → hỏi lại ngay, không chờ lượt poll.
  useEffect(() => {
    const onLimit = () => { fetchTodayTimeFromServer(); };
    window.addEventListener(DAILY_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(DAILY_LIMIT_EVENT, onLimit);
  }, [fetchTodayTimeFromServer]);

  // Poll thời gian học từ server mỗi POLL_INTERVAL. Đang bị chặn thì hỏi
  // thưa hơn (mỗi BLOCKED_POLL_EVERY lượt) — vẫn hỏi để tự gỡ khi admin mở
  // khoá / sang ngày, nhưng không tốn lượt gọi Moodle cho HV đã đủ giờ.
  useEffect(() => {
    if (!student?.elearningUserId) return;

    fetchTodayTimeFromServer();
    let tick = 0;
    const intervalId = setInterval(() => {
      tick += 1;
      if (!isLimitExceededRef.current || tick % BLOCKED_POLL_EVERY === 0) {
        fetchTodayTimeFromServer();
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [student?.elearningUserId, fetchTodayTimeFromServer]);

  // Đang bị chặn → hẹn kiểm lại lúc 00:01:30 VN (bộ đếm đã sang ngày, job
  // 00:01 đã mở khoá) để tab mở qua nửa đêm tự gỡ chặn.
  // Lượt đó lỗi (429, mạng) hoặc vẫn chặn → hỏi lại thưa dần vài lần.
  useEffect(() => {
    if (!isLimitExceeded || !student?.elearningUserId) return undefined;
    let timer;
    let attempt = 0;
    const check = async () => {
      try { localStorage.removeItem('elearning_limit_blocked'); } catch {}
      const blocked = await fetchTodayTimeFromServer();
      if (blocked === false) return;
      const delay = midnightRetryDelayMs(attempt);
      attempt += 1;
      if (delay != null) timer = setTimeout(check, delay);
    };
    timer = setTimeout(check, msUntilNextVnReset());
    return () => clearTimeout(timer);
  }, [isLimitExceeded, student?.elearningUserId, fetchTodayTimeFromServer]);

  // Máy ngủ qua đêm thì setTimeout bị trễ — mở lại tab thì xét ngay.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !isLimitExceededRef.current) return;
      try {
        const saved = JSON.parse(localStorage.getItem('elearning_limit_blocked'));
        if (!saved || isSavedBlockStale(saved)) {
          localStorage.removeItem('elearning_limit_blocked');
          fetchTodayTimeFromServer();
        }
      } catch {}
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchTodayTimeFromServer]);
  
  const { isKicked, isTerminated, isDenied, reason } = useElearningSession();

  const [isCollapsed, setIsCollapsed] = React.useState(
    window.location.pathname?.includes('/elearning/student/book')
  );

  useEffect(() => {
    // Phase 2D step 5: bootstrap on either token. New FE tabs only have
    // sessionToken (BE no longer returns moodleToken at login); old tabs
    // may still hold moodleToken in localStorage. Either is sufficient
    // for the bootstrap call — the controller resolves driving from
    // whichever signal is present.
    const sessionToken = localStorage.getItem('sessionToken');
    const moodleToken = localStorage.getItem('moodleToken');
    if (!sessionToken && !moodleToken) {
      window.location.href = '/elearning/login';
      return;
    }

    elearningApi
      .getUserByMoodleToken(moodleToken)
      .then((res) => {
        // BE may still issue a fresh sessionToken (legacy 7d JWT for
        // socket auth) — persist it so the socket can handshake without
        // needing a Moodle round-trip on its own.
        if (res.data?.sessionToken) {
          localStorage.setItem('sessionToken', res.data.sessionToken);
        }
        dispatch(
          updateElearningData({
            elearningUser: res.data,
          })
        );
      })
      .catch((error) => {
        navigate(PATH.ELEARNING.LOGIN);
        toastWrapper('Phiên làm việc đã hết hạn. Vui lòng đăng nhập lại.', 'error');
      });
  }, []);

  useEffect(() => {
    if (window.location.pathname?.includes('/elearning/student/book')) {
      setIsCollapsed(true);
    }
  }, []);

  const bookIds = useMemo(() => {
    if (!activityReport || typeof activityReport !== 'object') return [];

    return Object.values(activityReport).flatMap((activities) =>
      Array.isArray(activities)
        ? activities
            .filter((a) => a?.modname === 'book' && a?.cmid)
            .map((a) => a.cmid)
        : []
    );
  }, [activityReport]);


  const quizIds = useMemo(() => {
    if (!elearningGrades) return [];
    return Object.values(elearningGrades || {}).reduce((acc, grade) => {
      if (grade?.modules) {
        Object.keys(grade?.modules || {}).forEach((cmid) => {
          const module = grade.modules[cmid];
          if (module?.modname === 'quiz' && module?.quiztimelimit === 0) {
            acc.push(module?.cminstance);
          }
        });
      }
      return acc;
    }, []);
  }, [elearningGrades]);

  useEffect(() => {
    const fetchQuizAttempts = async (quizIds) => {
      try {
        const responses = await Promise.all(
          quizIds?.map((quizId) =>
            elearningApi
              .getUserQuizAttempts(student?.elearningUserId, quizId)
              .then((res) => ({ quizId, data: res?.data || {} }))
              .catch((error) => {
                console.error(`Error fetching quiz ${quizId}:`, error);
                return { quizId, data: {} };
              })
          )
        );

        const quizAttempts = {};
        responses?.forEach(({ quizId, data }) => {
          quizAttempts[quizId] = data;
        });

        dispatch(
          updateElearningData({
            quizAttempts,
          })
        );
      } catch (error) {
        console.error('Error fetching quiz attempts:', error);
        toastWrapper(
          'Có lỗi xảy ra trong quá trình tải dữ liệu. Vui lòng thử lại.',
          'error'
        );
      }
    };

    if (
      Array.isArray(quizIds) &&
      quizIds.length > 0 &&
      student?.elearningUserId
    ) {
      fetchQuizAttempts(quizIds);
    }
  }, [quizIds, student?.elearningUserId]);

  useEffect(() => {
    const fetchBookTime = async (bookIds) => {
      try {
        // BATCH: trước đây map N call song song getModuleTime — student
        // có 5-10 book → 5-10 request hit BE cùng giây, cộng với spot
        // bookModuleSettings ở effect khác → 10-20 request /module/setting
        // trong 1 mount. Endpoint /module/settings/batch BE đã có, trả
        // về `{ [moduleId]: [...settings] }`.
        let batchData = {};
        try {
          const res = await elearningApi.getBatchModuleTime(bookIds);
          batchData = res?.data || {};
        } catch (error) {
          console.error('Error fetching batch book time:', error);
        }

        const responses = bookIds.map((bookId) => {
          const items = batchData[bookId] || [];
          const totalTime = items.reduce((acc, item) => {
            if (item?.readingTime) acc += item.readingTime;
            return acc;
          }, 0) || 0;
          return { bookId, totalTime };
        });

        const bookTime = {};
        responses?.forEach(({ bookId, totalTime }) => {
          bookTime[bookId] = totalTime;
        });
        dispatch(
          updateElearningData({
            bookTime,
          })
        );
      } catch (error) {
        console.error('Error fetching book time:', error);
        toastWrapper(
          'Có lỗi xảy ra trong quá trình tải dữ liệu. Vui lòng thử lại.',
          'error'
        );
      }
    };

    if (Array.isArray(bookIds) && bookIds.length > 0) fetchBookTime(bookIds);
  }, [bookIds?.length]);

  useEffect(() => {
    const fetchBookMapaData = async () => {
      try {
        const { startMs, endMs } = getTodayRangeUTC7();
        const res = await elearningApi.getStudentBookDataToday(
          student?._id,
          new Date(startMs).toISOString(),
          new Date(endMs).toISOString()
        );
        dispatch(
          updateElearningData({
            bookMapaData: res?.data || [],
          })
        );
      } catch (error) {
        console.error('Error fetching book mapa data:', error);
      }
    };

    if (student?._id) {
      fetchBookMapaData();
    }
  }, [student?._id]);

  const loadUserElearningData = async (lessonIds) => {
    try {
      const [userCourseGradeRes, activityReportRes] = await Promise.all([
        elearningApi.getUserCourseGrade([student?.elearningUserId]),
        elearningApi.getElearningActivityReport(lessonIds, [
          student?.elearningUserId,
        ]),
      ]);
      const elearningGrades = groupUserGradeByCourseModule(
        userCourseGradeRes?.data
      )[student?.elearningUserId]?.courses;
      const activityReport =
        activityReportRes?.data?.[student?.elearningUserId];
      dispatch(
        updateElearningData({
          elearningGrades,
          activityReport,
        })
      );
    } catch (error) {
      console.error('Error fetching elearning data:', error);
      toastWrapper(
        'Có lỗi xảy ra trong quá trình tải dữ liệu. Vui lòng thử lại.',
        'error'
      );
    }
  };

  useEffect(() => {
    const loadElearningCoursesData = async (lessonIds) => {
      try {
        // PERF FIX (N+1): trước đây dùng elearningApi.getCoursesContents()
        // — wrapper loop sequentially qua /elearning/course/content?courseId=X.
        // Mount /elearning/student/course với 6 lesson → 6 round-trip nối tiếp
        // (~600ms-1.5s tổng). Chuyển sang:
        //   1. /elearning/courses/:id/contents (proxy endpoint) — có BE cache
        //      SWR, rate-limit per-student, sessionAuth. Cùng payload Moodle
        //      core_course_get_contents.
        //   2. Promise.all để 6 request bay song song → giới hạn còi cọc
        //      bởi BE cache (call 2-N hit cache).
        // Cùng dữ liệu đã được trang ElearningStudentCoursePage refetch độc
        // lập trước đây qua endpoint này → giờ trang đó đọc trực tiếp từ
        // Redux courseContents thay vì fire thêm 6 call /courses/:id/contents.
        // BE batch endpoint cho course content chưa tồn tại (cần follow-up
        // commit ở uniapp-backend để thêm /elearning/courses/contents/batch
        // nếu muốn xuống 1 round-trip duy nhất).
        const safeLessonIds = Array.isArray(lessonIds) ? lessonIds : [];

        // BE giới hạn /courses/:id/contents ở 10 req/10s/HV. Bắn song song
        // TOÀN BỘ (Promise.all) làm HV có >10 course bị 429 ở các call vượt
        // ngưỡng → trả [] fallback → CourseCard kẹt loading. Chạy có giới hạn
        // đồng thời + retry-backoff khi 429 để mọi course tải đủ mà không gây
        // 429 dây chuyền. Thứ tự kết quả không quan trọng (downstream key theo
        // id), nên pool đơn giản là đủ.
        const fetchContentsThrottled = async (ids) => {
          const CONCURRENCY = 4;
          const MAX_RETRIES = 3;
          const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
          const out = [];
          let cursor = 0;
          const worker = async () => {
            while (cursor < ids.length) {
              const id = ids[cursor++];
              let data = [];
              for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                try {
                  data = await elearningApi.courseContents(id);
                  break;
                } catch (error) {
                  const is429 = error?.response?.status === 429;
                  if (is429 && attempt < MAX_RETRIES) {
                    // 3s/6s/9s — đủ để cửa sổ rate-limit 10s mở slot.
                    await sleep(3000 * (attempt + 1));
                    continue;
                  }
                  console.error(`Error fetching course content ${id}:`, error);
                  data = [];
                  break;
                }
              }
              out.push({ id, data });
            }
          };
          await Promise.all(
            Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker)
          );
          return out;
        };

        const [elearningCoursesRes, contentResults] = await Promise.all([
          // classification='all' — PHẢI 'all', KHÔNG dùng 'inprogress'.
          // 'inprogress' loại trừ course past (hết enddate) / future / HV
          // "removed from view" / completed → 1 số HV (course đã hết hạn
          // hoặc hoàn thành) nhận [] → course list + results TRỐNG. 'all'
          // trả mọi course HV enrol bất kể timeline → đầy đủ. (Trade-off:
          // 'all' chậm hơn vì Moodle scan nhiều; nếu cần tốc độ thì thêm
          // BE cache SWR cho /me/courses, KHÔNG đổi classification.)
          elearningApi.myEnrolledCourses('all'),
          fetchContentsThrottled(safeLessonIds),
        ]);
        const elearningCourses = {};
        elearningCoursesRes?.map((item) => {
          elearningCourses[item.id] = item;
        });

        // Gom result của Promise.all thành map {[lessonId]: sectionsArray}
        // để giữ nguyên downstream shape (groupCourseContent expects raw
        // sections array; CourseCard expects raw sections array).
        const elearningCoursesContentsRes = {};
        contentResults.forEach(({ id, data }) => {
          elearningCoursesContentsRes[id] = data;
        });

        const elearningCoursesContents = {};
        const courseContents = {};

        safeLessonIds.forEach((lessonId) => {
          elearningCoursesContents[lessonId] = groupCourseContent(
            elearningCoursesContentsRes[lessonId] || []
          );
          courseContents[lessonId] = elearningCoursesContentsRes[lessonId];
        });

        dispatch(
          updateElearningData({
            elearningCourses,
            elearningCoursesContents,
            courseContents,
          })
        );
      } catch (error) {
        console.error('Error fetching elearning courses data:', error);
        toastWrapper(
          'Có lỗi xảy ra trong quá trình tải dữ liệu. Vui lòng thử lại.',
          'error'
        );
      }
    };

    if (student) {
      const lessonIds = student?.elearningLessons;
      loadElearningCoursesData(lessonIds);
    }
  }, [student?.elearningUserId]);

  // Fetch book module settings (interval) khi elearningCoursesContents thay đổi
  useEffect(() => {
    if (!elearningCoursesContents) return;

    // Tìm tất cả book moduleIds từ elearningCoursesContents
    const bookModuleIds = [];
    Object.values(elearningCoursesContents).forEach((courseModules) => {
      Object.values(courseModules || {}).forEach((mod) => {
        if (mod?.modname === 'book' && mod?.id) {
          bookModuleIds.push(mod.id);
        }
      });
    });

    if (bookModuleIds.length === 0) return;

    const fetchBookModuleSettings = async () => {
      try {
        // BATCH: gom N call /module/setting?moduleId=X thành 1 call
        // /module/settings/batch?moduleIds=X,Y,Z. Cùng fix với
        // fetchBookTime ở effect trên.
        let batchData = {};
        try {
          const res = await elearningApi.getBatchModuleTime(bookModuleIds);
          batchData = res?.data || {};
        } catch (error) {
          console.error('Error fetching batch book module settings:', error);
        }

        // Tạo map: { [moduleId]: { [chapterId]: { interval, readingTime } } }
        const settings = {};
        bookModuleIds.forEach((moduleId) => {
          const items = batchData[moduleId] || [];
          settings[moduleId] = {};
          items.forEach((item) => {
            settings[moduleId][item?.chapterId] = {
              interval: item?.interval || 30,
              readingTime: item?.readingTime,
            };
          });
        });

        dispatch(updateElearningData({ bookModuleSettings: settings }));
      } catch (error) {
        console.error('Error fetching book module settings:', error);
      }
    };

    fetchBookModuleSettings();
  }, [elearningCoursesContents]);

  useEffect(() => {
    const fetchElearningSetting = async () => {
      try {
        const responses = await Promise.all(
          (student?.elearningLessons || []).map((cid) =>
            elearningApi
              .getElearningSetting(cid, student?.course?.drivingType)
              .then((res) => ({
                cid,
                setting: res?.data?.[0] || null,
              }))
              .catch((error) => {
                console.error(
                  `Error fetching setting for course ${cid}:`,
                  error
                );
                return { cid, setting: null };
              })
          )
        );

        const settings = {};
        responses?.forEach(({ cid, setting }) => {
          if (setting) {
            settings[cid] = setting;
          }
        });
        dispatch(
          updateElearningData({
            elearningSettings: settings,
          })
        );
      } catch (error) {
        console.error('Error fetching elearning settings:', error);
        toastWrapper(
          'Có lỗi xảy ra trong quá trình tải dữ liệu. Vui lòng thử lại.',
          'error'
        );
      }
    };

    if (student?.elearningLessons?.length > 0) {
      fetchElearningSetting();
    }
  }, [student?.elearningUserId]);

  return (
    <ChatProvider>
      <LayoutInner
        isKicked={isKicked}
        isTerminated={isTerminated}
        isDenied={isDenied}
        reason={reason}
        isCollapsed={isCollapsed}
        elearningUser={elearningUser}
        dispatch={dispatch}
      />
    </ChatProvider>
  );
}

function LayoutInner({ isKicked, isTerminated, isDenied, reason, isCollapsed, elearningUser, dispatch }) {
  // Ưu tiên terminated (admin kết thúc — không cho takeover) > kicked
  // (bị thiết bị khác giành) > denied (không giành được quyền học).
  const overlayVariant = isTerminated ? 'terminated' : isKicked ? 'kicked' : 'denied';
  const sessionBlocked = isKicked || isTerminated || isDenied;

  // Bị chặn phiên → UNMOUNT toàn bộ nội dung thay vì chỉ phủ overlay lên
  // trên: video player, quiz timer, polling interval trong page con chết
  // theo component → không còn request ghi tiến độ nào bắn từ nền bằng
  // sessionToken còn hạn. (BE còn lớp chặn thứ 2: requireSessionOwnership
  // trên video save / quiz save/finish — phòng bundle cũ/client không
  // hợp tác.)
  if (sessionBlocked) {
    return <SessionKickedOverlay reason={reason} variant={overlayVariant} />;
  }

  return (
    <>
      <InfoConfirmationRedirect />
      <AdminLayout
        isCollapsed={isCollapsed}
        menu={STUDENT_ELEARNING_MENU}
        title={<CenterLogo src={elearningUser?.center?.logo} />}
        handleLogout={() => {
          dispatch(
            updateElearningData({
              activityReport: null,
              elearningCourses: null,
              elearningCoursesContents: null,
              elearningGrades: null,
              elearningUser: null,
              elearningSettings: null,
              bookTime: null,
              bookMapaData: null,
              bookModuleSettings: null,
              quizAttempts: null,
              isLimitExceeded: false,
              timeLimitPerDay: null,
              totalTodayTime: null,
            })
          );
          // Best-effort revoke refresh token ở BE — không await để
          // logout UX không bị blocking nếu BE chậm. Lỗi mạng → token
          // vẫn còn ở DB nhưng đã clear FE; TTL 30d sẽ tự xoá.
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try { elearningApi.studentLogout(refreshToken).catch(() => {}); } catch (_) { /* noop */ }
          }
          localStorage.removeItem('moodleToken');
          localStorage.removeItem('moodleSiteInfo');
          localStorage.removeItem('forcePasswordChange');
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('rc-auth-token');
          localStorage.removeItem('rc-user-id');
          localStorage.removeItem('rc-username');
          localStorage.removeItem('rc-channel-id');
          window.location.reload();
        }}
      />
      <div
        style={{
          position: 'fixed',
          bottom: '50px',
          right: '10px',
          zIndex: 1000,
        }}
      >
        <div className='d-flex flex-column align-items-center' style={{ gap: 12 }}>
          <ChatPopup />
          <FloatingActionIcon
            href={`tel:${elearningUser?.center?.tel}`}
            aria-label='Gọi trung tâm'
          >
            <img src='/phone.png' alt='Phone' />
          </FloatingActionIcon>
          {/* Chỉ hiện khi trung tâm có số Zalo (trước đây luôn hiện, dẫn tới zalo.me/undefined). */}
          {centerZaloNumber(elearningUser?.center) && (
            <FloatingActionIcon
              href={`https://zalo.me/${centerZaloNumber(elearningUser?.center)}`}
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Chat Zalo'
            >
              <img src='/zalo.png' alt='Zalo' />
            </FloatingActionIcon>
          )}
        </div>
      </div>
    </>
  );
}

export default StudentElearningLayout;
